import Field, { AggregationType, FieldType, TYPE_ITEMS_FIELD_KEY, TYPE_ITEMS_PERIOD_TYPE } from "@dto/fields";
import { aggregationToPeriodType, normalizePeriod, Period, PeriodType, PeriodTypeDay } from "@dto/period";
import { Filter, IN } from "@dto/records";

// Aggregations grouping the rows by calendar period. aggregationToPeriodType
// falls back to Day for any other aggregation, so callers must check first.
const PERIOD_AGGREGATIONS: AggregationType[] = [AggregationType.WEEK, AggregationType.MONTH, AggregationType.QUARTER, AggregationType.YEAR];

// A relation chain is never that deep: bounds the link resolution.
const MAX_LINK_DEPTH = 5;

export function isPeriodAggregation(aggregation: AggregationType | null | undefined): boolean {
    return aggregation !== null && aggregation !== undefined && PERIOD_AGGREGATIONS.includes(aggregation);
}

// Granularity of the values a field stores: a DATE holds days, a PERIOD field
// its configured period type, a link the granularity of the field it displays
// (resolved like fieldToOutputType). The result may be 0 (Day): compare it to
// undefined, never rely on its truthiness.
export function fieldPeriodTypeOf(field: Field | null | undefined, fieldByKeys?: Map<string, Field> | null, depth = 0): PeriodType | undefined {
    if (!field) {
        return undefined;
    }
    switch (field.type) {
        case FieldType.Date:
            return PeriodTypeDay;
        case FieldType.Period:
            return field.typeItems?.get(TYPE_ITEMS_PERIOD_TYPE) ?? undefined;
        case FieldType.Link: {
            if (depth >= MAX_LINK_DEPTH) {
                return undefined;
            }
            const destFieldKey = field.typeItems?.get(TYPE_ITEMS_FIELD_KEY);
            const destField = destFieldKey ? fieldByKeys?.get(destFieldKey) : undefined;
            return fieldPeriodTypeOf(destField, fieldByKeys, depth + 1);
        }
        default:
            return undefined;
    }
}

// Granularity of the periods the column displays: the period aggregation when
// there is one (a quarter aggregated by month shows as its first month), else
// the field granularity. The filter editor proposes it and refuses the finer
// types: filtering on months while seeing quarters makes no sense visually.
export function displayedPeriodType(field: Field | null | undefined, aggregation: AggregationType | null | undefined, fieldByKeys?: Map<string, Field> | null): PeriodType | undefined {
    if (isPeriodAggregation(aggregation)) {
        return aggregationToPeriodType(aggregation!);
    }
    return fieldPeriodTypeOf(field, fieldByKeys);
}

// Finest period type a filter can match: the stored granularity, or the
// displayed one when it is finer (the backend then filters the displayed
// periods). The advanced editor (formula shortcuts) may go finer than the
// displayed granularity, down to this one.
export function matchablePeriodType(field: Field | null | undefined, aggregation: AggregationType | null | undefined, fieldByKeys?: Map<string, Field> | null): PeriodType | undefined {
    const stored = fieldPeriodTypeOf(field, fieldByKeys);
    if (isPeriodAggregation(aggregation)) {
        const displayed = aggregationToPeriodType(aggregation!);
        return stored === undefined ? displayed : Math.min(stored, displayed) as PeriodType;
    }
    return stored;
}

// The period of the given type containing today (its first day), as the
// period editors propose it by default.
export function currentPeriodOf(periodType: PeriodType): Period {
    return normalizePeriod(Period.today(), periodType);
}

// A filter period type is selectable only when it is at least as coarse as the
// displayed granularity (see displayedPeriodType).
export function isFilterPeriodTypeAllowed(periodType: PeriodType, displayedType: PeriodType | undefined): boolean {
    return displayedType === undefined || periodType >= displayedType;
}

// True when the per-column group filter holds an IN single filter with a
// period value (as opposed to an IN formula or a "from ... to" date range):
// such a filter needs the period editor whatever the current aggregation.
export function hasPeriodValueFilter(filter: Filter | null | undefined): boolean {
    const filters = filter?.groupFilter?.filters;
    if (!filters) {
        return false;
    }
    return filters.some(f => f.singleFilter?.operator === IN && f.singleFilter.value !== null && f.singleFilter.value !== undefined);
}
